import { ExportOutlined } from '@ant-design/icons';
import { useNavigate } from '@tanstack/react-router';
import { useFlowContext } from '_/contexts/FlowContext';
import { IFlowSettingsPageItem } from '_/features/flowSettings/flowSettings.types';
import { useCreateFlowSegmentSettings, useDeleteFlowSegmentSettings } from '_/hooks/useFlows';
import useToggleValue from '_/hooks/useToggleValue';
import { Button, Switch, Tooltip, Typography, Modal } from 'antd';
import React, { ReactElement, useCallback } from 'react';

const FlowSettingsItem = ({ defaultSettingsHref, id, settings, settingsHref, header }: IFlowSettingsPageItem): ReactElement => {
  const history = useNavigate();
  const { flowId } = useFlowContext();
  const [open, toggleOpen] = useToggleValue();

  const { mutate: createSettings } = useCreateFlowSegmentSettings(id, flowId);
  const { mutate: deleteSettings } = useDeleteFlowSegmentSettings(id, flowId);

  const unlinked = !!settings;

  const title = unlinked ? 'Reset to Default Flow Settings' : 'Create Custom Flow Settings';

  const unlinkTooltip = `Click to ${unlinked ? 'create custom settings' : 'reset to default'} settings`;

  const description = unlinked
    ? `Are you sure that you want to delete all custom settings for "${header}"?`
    : `Are you sure that you want to create the "${header}" custom settings?`;

  const onEditClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      void history({ to: settingsHref });
    },
    [history, settingsHref],
  );

  const onOpenSettingsClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const collectionSettingsPage = `${window.location.origin}${defaultSettingsHref}`;
      window.open(collectionSettingsPage, '_blank');
    },
    [defaultSettingsHref],
  );

  const onSwitchClick = useCallback(
    (checked: boolean, event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      toggleOpen();
    },
    [toggleOpen],
  );

  const handleSwitchContainerClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleConfirm = useCallback(() => {
    if (unlinked) {
      // Delete custom settings (link back to collection defaults)
      deleteSettings(undefined, {
        onSuccess: () => {
          toggleOpen();
        },
      });
    } else {
      // Create custom settings (unlink from collection defaults). This list-level toggle
      // represents "does this segment have any custom overrides at all", not a specific
      // section's fields, so an empty seed is correct here -- the per-section cards seed
      // their own real defaults once the user drills into "Edit".
      createSettings(
        {},
        {
          onSuccess: () => {
            toggleOpen();
          },
        },
      );
    }
  }, [toggleOpen, unlinked, createSettings, deleteSettings]);

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Typography.Text>{header}</Typography.Text>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {unlinked && (
            <Tooltip title="Edit settings">
              <Button onClick={onEditClick} icon={<ExportOutlined />}>
                Edit
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Open default collection settings">
            <Button onClick={onOpenSettingsClick} icon={<ExportOutlined />}>
              Open default
            </Button>
          </Tooltip>
          <div
            style={{ display: 'flex', alignItems: 'center', marginLeft: 8, fontWeight: 'normal' }}
            onClick={handleSwitchContainerClick}
          >
            <div style={{ marginRight: '5px' }}>Default</div>
            <Tooltip title={unlinkTooltip}>
              <Switch checked={unlinked} onChange={onSwitchClick} />
            </Tooltip>
            <div style={{ marginLeft: '5px' }}>Custom</div>
          </div>
        </div>
      </div>
      <Modal open={open} title={title} onCancel={toggleOpen} onOk={handleConfirm}>
        <p>{description}</p>
      </Modal>
    </>
  );
};

export default FlowSettingsItem;
